import environment from "environment";
import { getRequestService } from "@fas/cpa-state-manager/services/request";
import type { Response } from "@fas/ui-framework";
import type { SelectedValuesType } from "@fas/cpa-state-manager/redux/reducers/linkGenerator/reducer";
import {
  MAP_API_TIMEOUT,
  NO_PAYMENT_DETAILS_MODAL,
  optionsList,
  NATURE_OF_TRAFFIC_MODAL,
  INFO_BANNER,
} from "../constants";
import type { SmartOfferType } from "../components/SmartlinkList/types/SmartlinkList.types";

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmation?: string;
  imType: string;
  imName: string;
  howFoundOut: string;
  photo: string;
};
export type UserContactInfo = {
  companyName: string;
  companyUrl: string;
  address: string;
  country: string;
  city: string;
  state: string;
  zip: string;
  taxSSN: string;
  phoneNumber: string;
};
export type Manager = {
  name: string;
  email: string;
  skype: string;
  teams: string;
  telegram: string;
  photo: string;
};
export type PaymentInfoType = {
  bankAccountName: string;
  bankAddress: string;
  beAddress: string;
  beName: string;
  swiftCode: string;
  beCountry: string;
  iban: string;
  payoutCountry: string;
  accountNumber: string;
  bankName: string;
  paymentNote: string;
  paxumMail: string;
  webmoneyWallet: string;
  bitcoin: string;
  qiwi: string;
  yandex: string;
  capitalistWallet: string;
  payoneerWallet: string;
  payPalWallet: string;
  genome: string;
  paysera: string;
  usdt: string;
  vatNumber: string;
  paymentMethodId: number;
  isEdit: boolean;
  currency: string;
};
export type DashboardItemType = {
  date: string;
  smartClicks: number;
  leads: number;
};
export type Offer = {
  country: string;
  webMetric: string | number;
  mobMetric: string | number;
};
const requestService = getRequestService();

// eslint-disable-next-line no-underscore-dangle
(requestService as any)?._instance?.interceptors?.request?.use(
  (config: any) => {
    const authHeader = config.headers?.Authorization || config.headers?.authorization;
    const token = authHeader?.split(" ")[1];
    if (!token) {
      // eslint-disable-next-line no-underscore-dangle
      (requestService as any)._customAttr?.redirect?.();
      return Promise.reject(new Error("No token, go to login"));
    }
    return config;
  }
);
export const getUserInfo = () =>
  requestService
    .get(environment.endpoints.get.userInfo)
    .then(({ data }: Response<{ data: User }>) => data);
export const getManagerInfo = () =>
  requestService
    .get(environment.endpoints.get.managerInfo)
    .then(({ data }: Response<{ data: Manager }>) => data);
export const postUserInfo = (user: FormData) =>
  requestService.post(environment.endpoints.post.userInfo, user, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
export const getUserContactInfo = () =>
  requestService
    .get(environment.endpoints.get.contactInfo)
    .then(
      ({
        data: response,
      }: Response<{ data: UserContactInfo }>): UserContactInfo => response.data
    );

export const postUserContactInfo = (contactInfo: Partial<UserContactInfo>) =>
  requestService.post(environment.endpoints.post.contactInfo, contactInfo);

export const getPaymentInfo = () =>
  requestService
    .get(environment.endpoints.get.getPaymentInfo)
    .then(
      ({
        data: response,
      }: Response<{ data: PaymentInfoType }>): PaymentInfoType => response.data
    );

export const postPaymentInfo = (
  data: Omit<PaymentInfoType, "isEdit" | "currency">
) => requestService.post(environment.endpoints.post.paymentInfo, data);

export const getOutstandingBalance = () =>
  requestService
    .get(environment.endpoints.get.outstandingBalance, {
      timeout: MAP_API_TIMEOUT,
    })
    .then(
      ({
        data: response,
      }: Response<{ data: { outBalance: string } }>): string =>
        response.data.outBalance
    );
export const getDashboardData = (data: {
  filters: {
    date: [
      {
        from: string;
        to: string;
      },
    ];
  };
  group: [string];
}) =>
  requestService
    .post(environment.endpoints.post.getDashboardData, data, {
      timeout: MAP_API_TIMEOUT,
    })
    .then(
      ({
        data: response,
      }: Response<{ data: DashboardItemType[] }>): DashboardItemType[] =>
        response.data
    );

export const getBestOffers = () =>
  requestService.get(environment.endpoints.get.topOffers).then(
    ({
      data: response,
    }: Response<{
      title: string;
      data: Offer[];
    }>): {
      title: string;
      data: Offer[];
    } => response
  );
export const smartOfferCheck = (params: SelectedValuesType) =>
  requestService
    .get(environment.endpoints.get.smartOfferCheck, {
      params,
    })
    .then(
      ({
        data: { status },
      }: {
        data: {
          status: boolean;
        };
      }): boolean => status
    );

function prepareSmartOfferParams(
  data: SelectedValuesType
): Record<string, number> {
  return Object.keys(data).reduce(
    (acc: Record<string, number>, key: string): Record<string, number> => ({
      ...acc,
      [key]: Number(data[key]),
    }),
    {}
  );
}

type SmartOfferIncoming = {
  link: string;
  name: string;
  isTraffic: 1 | 0;
  isStraight: 1 | 0;
  isMainstream: 1 | 0;
  isPreLander: 1 | 0;
  isPPL: 1 | 0;
};
export const generateSmartOffer = (params: SelectedValuesType) =>
  requestService
    .put(
      environment.endpoints.put.generateSmartOffer,
      prepareSmartOfferParams(params)
    )
    .then(
      ({
        data: { data },
      }: {
        data: {
          data: SmartOfferIncoming;
        };
      }): SmartOfferType => prepareSmartOfferOptions(data)
    );

function prepareSmartOfferOptions(offer: SmartOfferIncoming): SmartOfferType {
  return {
    link: offer.link,
    name: offer.name,
    options: [
      "isTraffic" as const,
      "isStraight" as const,
      "isMainstream" as const,
      "isPreLander" as const,
      "isPPL" as const,
    ]
      .filter((k) => offer[k] !== undefined)
      .map((k) => optionsList[k][offer[k]] && optionsList[k][offer[k]].title),
  };
}

export const getAffiliateSmartOfferList = () =>
  requestService.get(environment.endpoints.get.getSmartOfferList).then(
    ({
      data: { data },
    }: {
      data: {
        data: SmartOfferIncoming[];
      };
    }): SmartOfferType[] => data.map(prepareSmartOfferOptions)
  );
export const getManagerCustomLinkList = () =>
  requestService.get(environment.endpoints.get.getCustomLinkList).then(
    ({
      data: { data },
    }: {
      data: {
        data: SmartOfferIncoming[];
      };
    }): SmartOfferType[] => data.map(prepareSmartOfferOptions)
  );
type ModalFlags = {
  showSourceComplianceModal: boolean;
  showPaymentInfoModal: boolean;
  showInfoBanner: boolean;
};
export const getModalFlags = () =>
  requestService
    .get(environment.endpoints.get.getModals)
    .then(({ data }: { data: ModalFlags }) => ({
      [NATURE_OF_TRAFFIC_MODAL]: data.showSourceComplianceModal,
      [NO_PAYMENT_DETAILS_MODAL]: data.showPaymentInfoModal,
      [INFO_BANNER]: data.showInfoBanner,
    }));

export const getNatureOfTraffic = () =>
  requestService.get(environment.endpoints.get.getNatureOfTraffic).then(
    ({
      data: { natureOfTraffic },
    }: {
      data: {
        natureOfTraffic: string;
      };
    }): string => natureOfTraffic
  );

export const updateNatureOfTraffic = (
  natureOfTraffic: string,
  isSourceConfirmed: boolean
) =>
  requestService.post(environment.endpoints.post.updateNatureOfTraffic, {
    natureOfTraffic,
    isSourceConfirmed,
  });
export const updateInfoBanner = (email: string, isBannerAccepted: boolean) =>
  requestService.post(environment.endpoints.post.updateInfoBanner, {
    email,
    isBannerAccepted,
  });
